import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import workspaceAPI from '../services/workspaceAPI';
import { syncWorkspaceAccessFromResponse } from '../utils/workspaceSession';

const WorkspaceContext = createContext({
  loading: true,
  error: '',
  permissions: {},
  stats: null,
  company: null,
  userRole: '',
  workspaceAccess: null,
  refresh: () => {},
});

export const useWorkspace = () => useContext(WorkspaceContext);

const syncUserRole = (userRole) => {
  if (!userRole) return;
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user || user.role === userRole) return;
    user.role = userRole;
    localStorage.setItem('user', JSON.stringify(user));
    window.dispatchEvent(new Event('workspace-user-updated'));
  } catch {
    /* ignore */
  }
};

const WORKSPACE_ROLES = new Set(['company_admin', 'company_manager', 'company_staff']);

const canLoadWorkspaceDashboard = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return WORKSPACE_ROLES.has(String(user?.role || ''));
  } catch {
    return false;
  }
};

export const WorkspaceProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissions, setPermissions] = useState({});
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [workspaceAccess, setWorkspaceAccess] = useState(null);

  const load = useCallback(async () => {
    if (!canLoadWorkspaceDashboard()) {
      setLoading(false);
      setError('');
      setPermissions({});
      setStats(null);
      setCompany(null);
      setUserRole('');
      setWorkspaceAccess(null);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await workspaceAPI.getDashboard();
      const {
        permissions: perms,
        stats: dashboardStats,
        company: co,
        userRole: role,
        workspaceAccess: accessMeta,
      } = res.data;
      setPermissions(perms || {});
      setStats(dashboardStats || null);
      setCompany(co || null);
      setUserRole(role || '');
      setWorkspaceAccess(accessMeta || null);
      syncWorkspaceAccessFromResponse({
        user: { role },
        workspaceAccess: accessMeta,
      });
      syncUserRole(role);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <WorkspaceContext.Provider
      value={{
        loading,
        error,
        permissions,
        stats,
        company,
        userRole,
        workspaceAccess,
        refresh: load,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
